import { c as _c } from "react/compiler-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { COLORS } from '@/styles/tokens/colors';
import { RECURSIVE_FORM_LIMITS } from '../recursiveFormConfig';
import { AnswerReviewFieldRow } from './AnswerReviewFieldRow';
import type { AnswerReviewEditEvent, AnswerReviewFilter, AnswerReviewWorkbenchProps } from './AnswerReviewWorkbench.types';
import { recursiveNodeMatchesFilter, recursiveReviewField, recursiveReviewFields } from './AnswerReviewWorkbench.utils';
import { answerTargetKey, createDraftItemKey, emptyAnswerNode, flattenAnswerNodes, isDraftItemKey, RecursiveAnswerNode, recursiveAnswerValidationMessage } from './recursiveAnswer';
import { RecursiveReviewItem } from './RecursiveReviewItem';
import { itemsNeedingAttention } from './recursiveReviewSummary';
function newItemNode(schema: RecursiveAnswerNode['schema'], key: string): RecursiveAnswerNode {
  const node = emptyAnswerNode(schema, key);
  if (node.kind !== 'object' || schema.fieldType !== 'object') {
    return node;
  }
  // Open a draft scaffold for immediate typing. Serialization still rejects
  // all-missing objects, so it cannot persist until the user enters a property.
  return {
    ...node,
    isNull: false,
    properties: schema.properties.map(property => emptyAnswerNode(property, key))
  };
}
type CitationProps = Pick<AnswerReviewWorkbenchProps, 'onSelectEvidence' | 'selectedEvidence' | 'onFocusField' | 'onCloseField' | 'onCancelCitation' | 'onAddCitation' | 'focusedFieldId' | 'citationAction' | 'onAnswerBlur'>;

/** Prefer a value input; an array instead offers its add-item button. */
function firstEditableTarget(node: RecursiveAnswerNode): string | undefined {
  const nodes = flattenAnswerNodes(node);
  const first = nodes.find(entry => entry.kind === 'simple') ?? nodes.find(entry => entry.kind === 'array' || entry.isNull);
  return first && answerTargetKey(first.target);
}
export function RecursiveAnswerEditor(t0) {
  const $ = _c(141);
  let autoFocusTarget;
  let citations;
  let expansion;
  let node;
  let onChange;
  let onEditingChange;
  let readOnly;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      node,
      filter: t1,
      retainedFieldIds: t2,
      expansion,
      itemRoot: t3,
      nested: t4,
      autoFocusTarget,
      readOnly,
      onChange,
      onEditingChange,
      ...citations
    } = t0);
    $[0] = t0;
    $[1] = autoFocusTarget;
    $[2] = citations;
    $[3] = expansion;
    $[4] = node;
    $[5] = onChange;
    $[6] = onEditingChange;
    $[7] = readOnly;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    autoFocusTarget = $[1];
    citations = $[2];
    expansion = $[3];
    node = $[4];
    onChange = $[5];
    onEditingChange = $[6];
    readOnly = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  const filter = t1 === undefined ? "all" : t1;
  let t5;
  if ($[12] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[12] = t2;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const retainedFieldIds = t5;
  const itemRoot = t3 === undefined ? false : t3;
  const nested = t4 === undefined ? false : t4;
  const childrenId = useId();
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const [items, setItems] = useState(t6);
  let t7;
  if ($[15] !== expansion || $[16] !== items) {
    t7 = expansion ?? {
      items,
      setItem: (key, expanded) => setItems(current => ({
        ...current,
        [key]: expanded
      }))
    };
    $[15] = expansion;
    $[16] = items;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const itemExpansion = t7;
  const [newItemFocusTarget, setNewItemFocusTarget] = useState();
  const focusTarget = newItemFocusTarget ?? autoFocusTarget;
  if (!recursiveNodeMatchesFilter(node, filter, retainedFieldIds)) {
    return null;
  }
  let t8;
  if ($[18] !== node) {
    t8 = recursiveReviewField(node);
    $[18] = node;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const field = t8;
  const focused = citations.focusedFieldId;
  const showAnswerRow = node.kind === "simple" || node.isNull || node.kind === "array" && node.items.length === 0;
  let t9;
  if ($[20] !== focused || $[21] !== node) {
    let t10;
    if ($[23] !== focused) {
      t10 = entry => entry.id === focused;
      $[23] = focused;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t9 = recursiveReviewFields(node).some(t10);
    $[20] = focused;
    $[21] = node;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const containsFocusedField = t9;
  const expanded_0 = itemRoot || showAnswerRow || containsFocusedField || (itemExpansion.items[field.id] ?? true);
  const isRootCollection = !nested && node.kind !== "simple";
  const showCollectionHeader = node.kind === "array" || node.kind === "object" && !showAnswerRow && !itemRoot;
  let T0;
  let T1;
  let changed;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[25] !== childrenId || $[26] !== citations || $[27] !== expanded_0 || $[28] !== field || $[29] !== filter || $[30] !== focusTarget || $[31] !== focused || $[32] !== isRootCollection || $[33] !== itemExpansion || $[34] !== itemRoot || $[35] !== nested || $[36] !== node || $[37] !== onChange || $[38] !== onEditingChange || $[39] !== readOnly || $[40] !== retainedFieldIds || $[41] !== showAnswerRow || $[42] !== showCollectionHeader) {
    let properties = [];
    if (node.kind === "object") {
      properties = node.properties;
      if (node.isNull && node.schema.fieldType === "object") {
        let t22;
        if ($[58] !== node.target) {
          t22 = schema => emptyAnswerNode(schema, node.target.arrayItemID);
          $[58] = node.target;
          $[59] = t22;
        } else {
          t22 = $[59];
        }
        properties = node.schema.properties.map(t22);
      }
    }
    let t22;
    if ($[60] !== onChange) {
      t22 = next => onChange({
        ...next,
        assessment: undefined
      }, "immediate");
      $[60] = onChange;
      $[61] = t22;
    } else {
      t22 = $[61];
    }
    changed = t22;
    let t23;
    if ($[62] !== changed || $[63] !== field.id || $[64] !== itemExpansion || $[65] !== node) {
      t23 = () => {
        if (node.kind !== "array" || node.schema.fieldType !== "array") {
          return;
        }
        const key_0 = createDraftItemKey();
        const itemNode = newItemNode(node.schema.items, key_0);
        setNewItemFocusTarget(firstEditableTarget(itemNode));
        itemExpansion.setItem(key_0, true);
        itemExpansion.setItem(field.id, true);
        changed({
          ...node,
          isNull: false,
          references: [],
          items: [...node.items, {
            id: null,
            key: key_0,
            node: itemNode
          }]
        });
      };
      $[62] = changed;
      $[63] = field.id;
      $[64] = itemExpansion;
      $[65] = node;
      $[66] = t23;
    } else {
      t23 = $[66];
    }
    const handleAddItem = t23;
    let t24;
    if ($[67] !== node || $[68] !== onChange) {
      t24 = t25 => {
        const {
          value,
          save
        } = t25;
        if (node.kind !== "simple" || typeof value === "number") {
          return;
        }
        onChange({
          ...node,
          value,
          isNull: value === null,
          assessment: undefined
        }, save);
      };
      $[67] = node;
      $[68] = onChange;
      $[69] = t24;
    } else {
      t24 = $[69];
    }
    const handleValueChange = t24;
    let t25;
    if ($[70] !== childrenId || $[71] !== expanded_0 || $[72] !== field.id || $[73] !== itemExpansion || $[74] !== itemRoot || $[75] !== node.schema || $[76] !== showAnswerRow) {
      t25 = !itemRoot && !showAnswerRow && <IconButton size="xs" variant="transparent" icon={expanded_0 ? ChevronDownIcon : ChevronRightIcon} ariaLabel={`${expanded_0 ? "Collapse" : "Expand"} ${node.schema.label}`} aria-expanded={expanded_0} aria-controls={childrenId} onClick={() => itemExpansion.setItem(field.id, !expanded_0)} />;
      $[70] = childrenId;
      $[71] = expanded_0;
      $[72] = field.id;
      $[73] = itemExpansion;
      $[74] = itemRoot;
      $[75] = node.schema;
      $[76] = showAnswerRow;
      $[77] = t25;
    } else {
      t25 = $[77];
    }
    const toggle = t25;
    let t26;
    if ($[78] !== field.id || $[79] !== focusTarget || $[80] !== handleAddItem || $[81] !== node.items || $[82] !== node.kind || $[83] !== readOnly) {
      t26 = node.kind === "array" && !readOnly && <Button data-add-array-item={true} size="xs" variant="transparent" startIcon={PlusIcon} autoFocus={focusTarget === field.id && !readOnly} disabled={node.items.length >= RECURSIVE_FORM_LIMITS.arrayItems} onClick={handleAddItem}>Add item</Button>;
      $[78] = field.id;
      $[79] = focusTarget;
      $[80] = handleAddItem;
      $[81] = node.items;
      $[82] = node.kind;
      $[83] = readOnly;
      $[84] = t26;
    } else {
      t26 = $[84];
    }
    const addItem = t26;
    const attention = itemsNeedingAttention(node);
    let t27;
    if ($[85] !== node) {
      t27 = node.kind === "simple" ? undefined : recursiveAnswerValidationMessage(node);
      $[85] = node;
      $[86] = t27;
    } else {
      t27 = $[86];
    }
    const validationMessage = t27;
    T1 = Stack;
    t13 = 0;
    t14 = field.id;
    t15 = isRootCollection ? "" : undefined;
    t16 = isRootCollection ? `1px solid ${COLORS.GRAY[200]}` : undefined;
    t17 = isRootCollection ? 1 : undefined;
    if ($[87] !== isRootCollection) {
      t18 = isRootCollection ? {
        overflow: "hidden"
      } : undefined;
      $[87] = isRootCollection;
      $[88] = t18;
    } else {
      t18 = $[88];
    }
    if ($[89] !== validationMessage) {
      t19 = validationMessage && <Typography variant="body-xs" role="alert" sx={{
        px: 2,
        py: 1,
        color: COLORS.FUNCTIONAL.ERROR[500]
      }}>{validationMessage}</Typography>;
      $[89] = validationMessage;
      $[90] = t19;
    } else {
      t19 = $[90];
    }
    if ($[91] !== addItem || $[92] !== attention || $[93] !== expanded_0 || $[94] !== nested || $[95] !== node.items || $[96] !== node.kind || $[97] !== node.schema || $[98] !== showCollectionHeader || $[99] !== toggle) {
      t20 = showCollectionHeader && <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} px={2} py={1.5} bgcolor={!nested ? COLORS.GRAY[50] : undefined} borderBottom={!nested && !expanded_0 ? undefined : `1px solid ${COLORS.GRAY[200]}`}><Box width={16} flexShrink={0} display="flex" justifyContent="center">{toggle}</Box><Stack minWidth={0} flex={1}><Typography variant="body-s" fontWeight={600} sx={{
            overflowWrap: "anywhere"
          }}>{node.schema.label}</Typography>{node.kind === "array" && <Typography variant="body-xs" sx={{
            color: COLORS.TEXT.SECONDARY
          }}>{node.items.length} {node.items.length === 1 ? "item" : "items"}{attention > 0 && <>{" "}- {attention}{" "}{attention === 1 ? "item needs" : "items need"} attention</>}</Typography>}</Stack><Box ml="auto">{addItem}</Box></Stack>;
      $[91] = addItem;
      $[92] = attention;
      $[93] = expanded_0;
      $[94] = nested;
      $[95] = node.items;
      $[96] = node.kind;
      $[97] = node.schema;
      $[98] = showCollectionHeader;
      $[99] = toggle;
      $[100] = t20;
    } else {
      t20 = $[100];
    }
    if ($[101] !== citations || $[102] !== field || $[103] !== focusTarget || $[104] !== focused || $[105] !== handleValueChange || $[106] !== node.target || $[107] !== onEditingChange || $[108] !== readOnly || $[109] !== showAnswerRow) {
      t21 = showAnswerRow && <AnswerReviewFieldRow collection={true} field={field} readOnly={readOnly} autoFocus={focusTarget === field.id && !readOnly} {...citations} onAddCitation={isDraftItemKey(node.target.arrayItemID) ? undefined : citations.onAddCitation} isFocused={focused === field.id} onEditingChange={onEditingChange} onAnswerChange={handleValueChange} />;
      $[101] = citations;
      $[102] = field;
      $[103] = focusTarget;
      $[104] = focused;
      $[105] = handleValueChange;
      $[106] = node.target;
      $[107] = onEditingChange;
      $[108] = readOnly;
      $[109] = showAnswerRow;
      $[110] = t21;
    } else {
      t21 = $[110];
    }
    T0 = Box;
    t10 = childrenId;
    t11 = !expanded_0;
    t12 = node.kind === "object" && <Stack minWidth={0}>{properties.map(property => <RecursiveAnswerEditor nested={true} autoFocusTarget={focusTarget} expansion={itemExpansion} filter={node.isNull ? "all" : filter} retainedFieldIds={retainedFieldIds} key={answerTargetKey(property.target)} node={property} readOnly={readOnly} {...citations} onEditingChange={onEditingChange} onChange={(updated, save_0) => onChange({
        ...node,
        isNull: false,
        assessment: node.isNull ? undefined : node.assessment,
        references: node.isNull ? [] : node.references,
        properties: properties.map(child => answerTargetKey(child.target) === answerTargetKey(property.target) ? updated : child)
      }, save_0)} />)}</Stack>;
    $[25] = childrenId;
    $[26] = citations;
    $[27] = expanded_0;
    $[28] = field;
    $[29] = filter;
    $[30] = focusTarget;
    $[31] = focused;
    $[32] = isRootCollection;
    $[33] = itemExpansion;
    $[34] = itemRoot;
    $[35] = nested;
    $[36] = node;
    $[37] = onChange;
    $[38] = onEditingChange;
    $[39] = readOnly;
    $[40] = retainedFieldIds;
    $[41] = showAnswerRow;
    $[42] = showCollectionHeader;
    $[43] = T0;
    $[44] = T1;
    $[45] = changed;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
  } else {
    T0 = $[43];
    T1 = $[44];
    changed = $[45];
    t10 = $[46];
    t11 = $[47];
    t12 = $[48];
    t13 = $[49];
    t14 = $[50];
    t15 = $[51];
    t16 = $[52];
    t17 = $[53];
    t18 = $[54];
    t19 = $[55];
    t20 = $[56];
    t21 = $[57];
  }
  let t22;
  if ($[111] !== changed || $[112] !== citations || $[113] !== filter || $[114] !== focusTarget || $[115] !== focused || $[116] !== itemExpansion || $[117] !== node || $[118] !== onChange || $[119] !== onEditingChange || $[120] !== readOnly || $[121] !== retainedFieldIds) {
    t22 = node.kind === "array" && <Stack minWidth={0}>{node.items.map((item, index) => recursiveNodeMatchesFilter(item.node, filter, retainedFieldIds) && <Box key={item.key} data-runtime-item={item.key}><RecursiveReviewItem key={item.key} node={item.node} position={index + 1} count={node.items.length} expanded={recursiveReviewFields(item.node).some(field_0 => field_0.id === focused) || (itemExpansion.items[item.key] ?? filter !== "all")} onToggle={() => itemExpansion.setItem(item.key, !(itemExpansion.items[item.key] ?? filter !== "all"))} onMove={readOnly || filter !== "all" ? undefined : direction => {
          const items_0 = [...node.items];
          items_0.splice(index, 1);
          items_0.splice(index + direction, 0, item);
          onChange({
            ...node,
            items: items_0
          }, "immediate");
        }} onDelete={readOnly ? undefined : () => changed({
          ...node,
          items: node.items.filter(entry_0 => entry_0.key !== item.key)
        })}><RecursiveAnswerEditor nested={true} itemRoot={true} autoFocusTarget={focusTarget} expansion={itemExpansion} filter={filter} retainedFieldIds={retainedFieldIds} node={item.node} readOnly={readOnly} {...citations} onEditingChange={onEditingChange} onChange={(updated_0, save_1) => onChange({
            ...node,
            items: node.items.map(entry_1 => entry_1.key === item.key ? {
              ...entry_1,
              node: updated_0
            } : entry_1)
          }, save_1)} /></RecursiveReviewItem></Box>)}</Stack>;
    $[111] = changed;
    $[112] = citations;
    $[113] = filter;
    $[114] = focusTarget;
    $[115] = focused;
    $[116] = itemExpansion;
    $[117] = node;
    $[118] = onChange;
    $[119] = onEditingChange;
    $[120] = readOnly;
    $[121] = retainedFieldIds;
    $[122] = t22;
  } else {
    t22 = $[122];
  }
  let t23;
  if ($[123] !== T0 || $[124] !== t10 || $[125] !== t11 || $[126] !== t12 || $[127] !== t22) {
    t23 = <T0 id={t10} hidden={t11}>{t12}{t22}</T0>;
    $[123] = T0;
    $[124] = t10;
    $[125] = t11;
    $[126] = t12;
    $[127] = t22;
    $[128] = t23;
  } else {
    t23 = $[128];
  }
  let t24;
  if ($[129] !== T1 || $[130] !== t13 || $[131] !== t14 || $[132] !== t15 || $[133] !== t16 || $[134] !== t17 || $[135] !== t18 || $[136] !== t19 || $[137] !== t20 || $[138] !== t21 || $[139] !== t23) {
    t24 = <T1 minWidth={t13} data-answer-target={t14} data-review-collection={t15} border={t16} borderRadius={t17} sx={t18}>{t19}{t20}{t21}{t23}</T1>;
    $[129] = T1;
    $[130] = t13;
    $[131] = t14;
    $[132] = t15;
    $[133] = t16;
    $[134] = t17;
    $[135] = t18;
    $[136] = t19;
    $[137] = t20;
    $[138] = t21;
    $[139] = t23;
    $[140] = t24;
  } else {
    t24 = $[140];
  }
  return t24;
}